import { PollsChoices } from "@virtualprogaming/js-public-sdk";
import classNames from "classnames";
import { find, isEmpty, isNil, map, noop, partial } from "lodash";
import moment from "moment";
import React, { FC, useMemo, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";

import { useSendMessage } from "~/hooks";
import { useCreateVote, useDeleteVote, useUpdatePollVote } from "~/queries";
import {
  PollResponseData,
  PollUser,
} from "~/queries/chat/poll/useChatConversationPoll";
import { Button } from "~/ui/components/buttons";
import { CDNImage, ImageVariant } from "~/ui/components/cdnImage";
import { Icon } from "~/ui/components/icons";
import { useChatAuthUserData } from "~/ui/context";
import { checkIsPollExpired, timeUntilPollExpires } from "~/utils";

export const Poll: FC<{
  poll: PollResponseData;
  memberCount: number;
  members: number[];
  chatId: number;
}> = ({ poll, memberCount, members, chatId }) => {
  const yesPercentage = (poll.yes / memberCount) * 100;
  const noPercentage = (poll.no / memberCount) * 100;
  const { chatAuthUser } = useChatAuthUserData();

  const openChoiceListRef = useRef<HTMLDivElement>(null);

  const [openedChoiceList, setOpenedChoiceList] = useState<PollsChoices | null>(
    null
  );
  const { mutateAsync: vote } = useCreateVote();
  const { mutateAsync: unvote } = useDeleteVote();
  const { mutateAsync: updateVote } = useUpdatePollVote();
  const { sendMessage } = useSendMessage(chatId);

  const hasVotedYes: boolean = useMemo(() => {
    const voted = find(
      poll.votes.yes,
      ({ id }) => id === Number(chatAuthUser?.user?.uid?.substring(1))
    );
    return !!voted;
  }, [chatAuthUser?.user?.uid, poll.votes.yes]);

  const hasVotedNo: boolean = useMemo(() => {
    const voted = find(
      poll.votes.no,
      ({ id }) => id === Number(chatAuthUser?.user?.uid?.substring(1))
    );
    return !!voted;
  }, [chatAuthUser?.user?.uid, poll.votes.no]);

  const handleUpdateVote = (choice: PollsChoices) => {
    updateVote([poll.poll.id, { choice }]).then(() => {
      const message = `${
        chatAuthUser?.user.username
      } updated his vote to ${choice.toUpperCase()} for Poll: ${
        poll.poll.title
      }.`;
      sendMessage(message, noop);
    });
  };
  const handleVote = (choice: PollsChoices) => {
    vote([poll.poll.id, { members, choice }]).then(() => {
      const message = `${
        chatAuthUser?.user.username
      } voted for ${choice.toUpperCase()} for Poll: ${poll.poll.title}.`;
      sendMessage(message, noop);
    });
  };
  const handleUnvote = (choice: PollsChoices) => {
    unvote(poll.poll.id).then(() => {
      const message = `${
        chatAuthUser?.user.username
      } removed his vote for ${choice.toUpperCase()} for Poll: ${
        poll.poll.title
      }.`;
      sendMessage(message, noop);
    });
  };

  const handleVotePress = (choice: PollsChoices) => {
    if (!hasVotedNo && !hasVotedYes) {
      handleVote(choice);
    }
    if (choice === PollsChoices.No && hasVotedNo) {
      handleUnvote(choice);
    }

    if (choice === PollsChoices.Yes && hasVotedYes) {
      handleUnvote(choice);
    }

    if (choice === PollsChoices.No && hasVotedYes && !hasVotedNo) {
      handleUpdateVote(PollsChoices.No);
    }
    if (choice === PollsChoices.Yes && !hasVotedYes && hasVotedNo) {
      handleUpdateVote(PollsChoices.Yes);
    }
  };

  const isPollExpired = useMemo(() => {
    return checkIsPollExpired(poll.poll.created_at, poll.poll.expires_in_days);
  }, [poll]);

  return (
    <div className="pd-sm">
      <h4 className="pd-b-sm">{poll?.poll.title}</h4>
      <div className="text-muted flex flex-col gap-sm">
        <span className="flex flex-row gap-xs">
          <span>
            <FormattedMessage defaultMessage="Created" id="ORGv1Q" />
          </span>
          <span> {moment(poll?.poll?.created_at).fromNow()}</span>
        </span>
        {!poll.poll.active && isPollExpired ? (
          <span className="flex flex-col gap-sm">
            <FormattedMessage defaultMessage="Poll has expired" id="LoXZ3L" />{" "}
          </span>
        ) : (
          <span className="flex flex-col gap-sm">
            <FormattedMessage defaultMessage="Expires in" id="3FWA2x" />{" "}
            {timeUntilPollExpires(
              poll?.poll?.created_at,
              poll?.poll?.expires_in_days ?? 1
            )}
          </span>
        )}
        {!poll?.poll.active && (
          <span className="flex flex-row items-center gap-sm">
            <Icon icon={["fas", "info-square"]} size="1x" />
            <FormattedMessage defaultMessage="Poll is closed." id="lgKEOF" />
          </span>
        )}
        {isPollExpired && poll?.poll.active && (
          <span className="flex flex-row items-center gap-sm">
            <Icon icon={["fas", "info-square"]} size="1x" />
            <FormattedMessage defaultMessage="Poll is expired." id="+ZMW41" />
          </span>
        )}
      </div>

      {isNil(openedChoiceList) ? (
        <div className="flex flex-col gap-md mg-t-sm">
          {Object.values(PollsChoices).map((choice) => (
            <div
              className="flex flex-col gap-md border borderR-05 bg-light pd-sm"
              key={choice}
              ref={openChoiceListRef}
            >
              <div className="flex justify-between">
                <span className="font-variant-big-caps text-success-light">
                  {choice}
                </span>

                <Button
                  onClick={partial(handleVotePress, choice)}
                  variant="success"
                  size="sm"
                  className="font-variant-small-caps flex items-center justify-center gap-md"
                  disabled={!poll.poll.active || isPollExpired}
                >
                  {(choice === PollsChoices.Yes ? hasVotedYes : hasVotedNo) ? (
                    <FormattedMessage defaultMessage="Unvote" id="6VGRlY" />
                  ) : (
                    <FormattedMessage defaultMessage="Vote" id="Y6dihM" />
                  )}
                </Button>
              </div>
              <div style={{ width: `${100}%` }} className="borderR-05 bg-dark">
                <div
                  style={{
                    width: `${
                      choice === PollsChoices.Yes ? yesPercentage : noPercentage
                    }%`,
                  }}
                  className={classNames(
                    "borderR-05",
                    (
                      choice === PollsChoices.Yes
                        ? yesPercentage > 0
                        : noPercentage > 0
                    )
                      ? "bg-success"
                      : "bg-light"
                  )}
                >
                  <span
                    className={classNames(
                      "pd-l-md",
                      choice === PollsChoices.Yes && poll.yes >= 1
                        ? "text-dark"
                        : "text-white",
                      choice === PollsChoices.No && poll.no >= 1
                        ? "text-dark"
                        : "text-white"
                    )}
                  >
                    {choice === PollsChoices.Yes ? poll.yes : poll.no}
                  </span>
                </div>
              </div>
              <span
                onClick={partial(setOpenedChoiceList, choice)}
                className="text-end font-variant-small-caps text-muted pointer"
              >
                <FormattedMessage defaultMessage="View votes" id="8hw8x1" />
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-sm border borderR-05 bg-light pd-sm h-max-300px scroll-auto scroll-hide mg-t-sm">
          <div className="w-100 pd-t-xs pd-b-sm flex items-center justify-between">
            <h6 className="pd-l-xs font-variant-small-caps">
              {openedChoiceList === PollsChoices.Yes ? (
                <FormattedMessage defaultMessage="Yes votes" id="t/baru" />
              ) : (
                <FormattedMessage defaultMessage="No votes" id="JNuVd0" />
              )}
            </h6>
            <Icon
              icon={["fal", "times"]}
              size="lg"
              className="pointer"
              onClick={partial(setOpenedChoiceList, null)}
            />
          </div>
          <div className="flex flex-col gap-sm h-max-300px scroll-auto scroll-hide">
            {(openedChoiceList === PollsChoices.Yes &&
              isEmpty(poll.votes.yes)) ||
            (openedChoiceList === PollsChoices.No && isEmpty(poll.votes.no)) ? (
              <div className="w-100 mg-t-none text-center pd-b-md">
                {openedChoiceList === PollsChoices.Yes ? (
                  <FormattedMessage
                    defaultMessage="There are no votes for option Yes."
                    id="lYwg3m"
                  />
                ) : (
                  <FormattedMessage
                    defaultMessage="There are no votes for option NO."
                    id="0JnvXI"
                  />
                )}
              </div>
            ) : (
              map(
                openedChoiceList === PollsChoices.Yes
                  ? poll.votes.yes
                  : poll.votes.no,
                (user: PollUser) => (
                  <div key={user.id} className="flex items-center gap-sm">
                    <CDNImage
                      imageId={user.image}
                      variant={ImageVariant.SmallThumbnail}
                      className="borderR-round"
                      width={30}
                      height={30}
                    />
                    <span>{user.username}</span>
                  </div>
                )
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};
